import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/shared";
import CompanyCard, { CompanyLogo } from "@/components/CompanyCard";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const SIZES = ["", "1-10", "11-50", "51-200", "201-1000", "1001-5000", "5000+"];

export function CompanyProfileEditor() {
  const { refresh } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = useRef();
  useEffect(() => { api.get("/recruiter/company").then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!c) return null;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/recruiter/company", { org_name: c.org_name, website: c.website, about: c.about, industry: c.industry, size: c.size, headquarters: c.headquarters }); setC(data); refresh(); toast.success("Company profile saved and shown on your job posts"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (f) => {
    if (!f) { return; }
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/recruiter/company/logo", fd); setC({ ...c, logo_url: data.logo_url }); toast.success("Logo updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel className="mb-6 border-t-4 border-t-teal-500" testid="company-profile-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Company profile</h2>
        {c.about && <Link to={`/company/${c.company_id}`} className="inline-flex items-center gap-1 text-sm text-teal-700 hover:underline" data-testid="company-public-link">View public page <ExternalLink className="h-3.5 w-3.5" /></Link>}
      </div>
      <p className="mb-4 text-xs text-muted-foreground">Shown on every job you post. Verified company domain: <b>{c.domain}</b></p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <div className="space-y-3">
          <div className="flex items-center gap-4"><CompanyLogo c={c} size="h-16 w-16" />
            <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="company-logo-input" />
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => file.current.click()} data-testid="company-logo-upload"><Upload className="mr-2 h-3.5 w-3.5" />Upload logo</Button>
            <span className="text-xs text-muted-foreground">PNG, JPG or WebP · 2 MB max</span></div>
          <Input placeholder="Company name" value={c.org_name} onChange={set("org_name")} data-testid="company-name-input" />
          <Input placeholder="https://yourcompany.com" value={c.website} onChange={set("website")} data-testid="company-website-input" />
          <div className="grid grid-cols-2 gap-3"><Input placeholder="Industry" value={c.industry} onChange={set("industry")} data-testid="company-industry-input" />
            <select className="rounded-md border bg-white px-3 text-sm" value={c.size} onChange={set("size")} data-testid="company-size-select">{SIZES.map((s) => <option key={s} value={s}>{s ? `${s} employees` : "Company size"}</option>)}</select></div>
          <Input placeholder="Headquarters (e.g. Dallas, TX)" value={c.headquarters} onChange={set("headquarters")} data-testid="company-hq-input" />
          <Textarea rows={4} maxLength={600} placeholder="A short profile: what you do, who you hire, why people like working with you (600 characters)" value={c.about} onChange={set("about")} data-testid="company-about-input" />
          <Button className="rounded-full" disabled={busy || c.org_name.length < 2} onClick={save} data-testid="company-save">Save profile</Button>
        </div>
        <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Preview on your job posts</p><CompanyCard c={{ ...c, about: c.about || "Your short company profile will appear here." }} link={false} /></div>
      </div>
    </Panel>
  );
}

export function HiringReportPreview() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/recruiter/hiring-report/preview").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  return (
    <Panel className="mb-6 border-t-4 border-t-rose-400" testid="hiring-report-preview">
      <h2 className="flex items-center gap-2 text-lg font-semibold"><Mail className="h-4 w-4 text-rose-500" />Your Monday hiring report</h2>
      <p className="text-xs text-muted-foreground">Emailed every Monday morning (Central time). Last 7 days: {r.period}</p>
      <div className="mt-4 grid grid-cols-3 gap-3 text-center text-white">
        {[[r.applicant_count, "New applicants", "bg-sky-500"], [r.accepted_count, "Accepted contacts", "bg-emerald-500"], [r.active_jobs, "Active posts", "bg-amber-500"]].map(([v, l, bg]) => (
          <div key={l} className={`rounded-xl p-3 ${bg}`}><p className="font-display text-2xl font-extrabold">{v}</p><p className="text-[11px]">{l}</p></div>))}
      </div>
      <div className="mt-4 space-y-1 text-sm">{r.applicants.slice(0, 3).map((a, i) => <p key={i}><b>{a.name}</b> · {a.job} · <span className="text-xs text-slate-500">{a.at}</span></p>)}
        {r.applicant_count === 0 && <p className="text-muted-foreground">No new applicants yet this week.</p>}</div>
    </Panel>
  );
}
